import { post as apiPost, TYPE_GOOGLE_RECAPTCHA_API, TYPE_WEB_APP } from 'api-client'
import { delay as wait, fromCallback as fromCallbackPromise } from 'bluebird'
import _get from 'lodash/get'
import _join from 'lodash/join'
import { getExponentialDelay } from 'exponential-backoff'
import { get as getConfig } from 'config'

// eslint-disable-next-line prefer-destructuring
const BROWSER = process.env.BROWSER

export const CAPTCHA_ACTION_BILLING_PAYMENT_FORM_VIEW = 'billing/paymentFormView'

export async function getGoogleRecaptchaScript (attempt = 0, maxAttempt = 3) {
  // bail if grecaptcha already exists
  if (!BROWSER || global.grecaptcha) {
    return
  }
  try {
    const { default: scriptjs } = await import('scriptjs')
    const { googleRecaptchaSiteKey } = getConfig()
    await fromCallbackPromise((cb) => scriptjs(`https://www.google.com/recaptcha/api.js?render=${googleRecaptchaSiteKey}`, () => cb()))
  } catch (e) {
    const { grecaptcha } = global
    // Check if Google recaptcha did load and try again
    /* eslint-disable no-useless-return */
    if (!grecaptcha && attempt < maxAttempt) {
      // Try to load the script again
      await wait(getExponentialDelay(attempt, 300))
      await getGoogleRecaptchaScript(attempt + 1, maxAttempt)
      return
    }

    if (!grecaptcha && attempt === maxAttempt) {
      // We tried too many times and the script didn't load, just give up
      return
    }
  }
}

/**
 * Verify Google recaptcha token
 * @param {Object} options The options
 * @param {String} options.googleRecaptchaSecretKey The secret key
 * @param {Number} options.googleRecaptchaThreshold The recaptcha threshold
 * @returns The Google recaptcha response
 */
export default async function verifyToken (options = {}) {
  const { token } = options
  const { googleRecaptchaSecretKey, googleRecaptchaThreshold } = getConfig()
  const params = {
    secret: googleRecaptchaSecretKey,
    response: token,
  }
  const response = await apiPost('siteverify', params, { timeout: 10000 }, TYPE_GOOGLE_RECAPTCHA_API)
  const success = _get(response, 'body.success')
  const score = _get(response, 'body.score')
  const action = _get(response, 'body.action', CAPTCHA_ACTION_BILLING_PAYMENT_FORM_VIEW)
  const hostname = _get(response, 'body.hostname')
  const errorCodes = _join(_get(response, 'body.error-codes'), ', ')
  /**
   * @see https://developers.google.com/recaptcha/docs/v3#site_verify_response
   */
  // eslint-disable-next-line max-len
  const valid = success && score >= googleRecaptchaThreshold && action === CAPTCHA_ACTION_BILLING_PAYMENT_FORM_VIEW

  if (!valid && BROWSER) {
    try {
      const { default: newrelicVendor } = await import('../../browser/new-relic')
      const { push: pushNewRelic } = newrelicVendor
      pushNewRelic((newrelic) => {
        newrelic.recordCustomEvent('BillingGoogleRecaptchaInvaild', {
          success,
          score,
          action,
          hostname,
          errorCodes,
        })
      })
    } catch (e) {
      // Do nothing on error
    }
  }

  return { valid }
}

/**
 * Get the Google recaptcha token
 * @param {Object} options The options
 * @param {String} options.token The Google Recaptcha token
 * @returns The Google recaptcha response
 */
export async function getCaptchaVerifyToken (options = {}) {
  const { token } = options
  try {
    const res = await apiPost('v1/captcha/verify-token', { googleRecaptchaToken: token }, { timeout: 10000 }, TYPE_WEB_APP)
    const { body } = res
    return body
  } catch (e) {
    return {}
  }
}
